import type RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type {
  UserAccountService,
  UserResponse,
} from 'walden-fast/types/fast-backend/user';
import { type User } from 'walden-fast/types/user';
import {
  SERVICE_NAMES,
  type ServiceName,
} from 'walden-fast/utils/constants/services';
import { userFactory } from 'walden-fast/utils/mocks';
import type SessionService from './session';
import type CacheManagerService from './cache-manager';
import type FastApiService from './fast-api';

export default class UserService extends Service {
  @service('fast-api') declare fastApiService: FastApiService;
  @service('router') declare routerService: RouterService;
  @service('session') declare sessionService: SessionService;
  @service('cache-manager') declare cacheManager: CacheManagerService;

  @tracked
  user: User | null = null;
  private inFlight?: Promise<User>;
  private readonly CACHE_KEY = 'user-data';
  private readonly CACHE_DURATION = 1 * 60 * 1000; // 1 minutes cache

  private readonly defaultServices = [
    SERVICE_NAMES.WALDEN_DASHBOARD,
    SERVICE_NAMES.WALDEN_CHAT,
  ] as const;

  async getUser(): Promise<User> {
    if (this.user) {
      return this.user;
    }
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = (async () => {
      const user = await this.fastApiService.get<UserResponse>(
        '/me',
        undefined,
        {
          key: this.CACHE_KEY,
          options: {
            duration: this.CACHE_DURATION,
            useImpersonationKey: true,
          },
        },
      );
      if (!user) {
        throw new Error('No user returned by api');
      }

      const availableServices = [...user.userAccount.services];
      this.addDefaultServices(availableServices);

      const result = userFactory({
        id: user.userProfile.id,
        firstName: user.userProfile.givenName,
        lastName: user.userProfile.surname,
        displayName: user.userProfile.displayName,
        email: user.userProfile.mail || '',
        enabledFeatures: user.userAccount.features || [],
        client: user.account ? user.account.displayName : '',
        clientCompanies: user.clientCompanies,
        availableServices,
        permissions: user.userAccount.permissions,
        preferredLanguage: user.userProfile.preferredLanguage || '',
        role: user.userAccount.role,
        account: user.account,
      });

      this.user = result;
      return result;
    })();

    try {
      return await this.inFlight;
    } finally {
      this.inFlight = undefined;
    }
  }

  resetUser() {
    this.user = null;
    // Clear cache for current impersonation context
    this.cacheManager.clear(this.CACHE_KEY);
  }

  reloadUser() {
    this.user = null;
    void this.getUser();
  }

  private addDefaultServices(services: UserAccountService[]): void {
    for (const serviceName of this.defaultServices) {
      const [serviceUniqueName, subServiceUniqueName] =
        this.parseServiceName(serviceName);

      const exists = services.some(
        (service) =>
          service.serviceUniqueName === serviceUniqueName &&
          service.subServiceUniqueName === subServiceUniqueName,
      );

      if (!exists) {
        services.push({
          active: true,
          serviceUniqueName,
          subServiceUniqueName,
          subscriptionPeriods: [
            {
              activeFromLocalTime: '1/01/2000',
              activeToLocalTime: '31/12/2099',
              name: 'nl-BE',
              format: 'd/MM/yyyy',
            },
          ],
        });
      }
    }
  }

  private parseServiceName(serviceName: string): [string, string] {
    const parts = serviceName.split('.');
    if (parts.length !== 2) {
      throw new Error(`Invalid service name format: ${serviceName}`);
    }
    return [parts[0] || '', parts[1] || ''];
  }

  async logout() {
    window.localStorage.removeItem('language');
    await this.sessionService.unauthenticate();
  }

  canAccessService(serviceName: ServiceName): boolean {
    if (!this.user) return false;

    if (this.isDefaultService(serviceName)) {
      return true;
    }

    const [serviceUniqueName, subServiceUniqueName] =
      this.parseServiceName(serviceName);
    return this.checkServiceAccess(serviceUniqueName, subServiceUniqueName);
  }

  needsToImpersonate() {
    if (!this.user) return false;
    return (
      this.user.role === 'WaldenAdministrator' ||
      this.user.role === 'WaldenUser'
    );
  }

  private isDefaultService(serviceName: ServiceName): boolean {
    return (this.defaultServices as readonly string[]).includes(serviceName);
  }

  private checkServiceAccess(
    serviceUniqueName: string,
    subServiceUniqueName: string,
  ): boolean {
    if (!this.user) return false;

    const now = new Date();

    return this.user.availableServices.some((service) => {
      if (!service.active) return false;

      const serviceMatches = service.serviceUniqueName === serviceUniqueName;
      const subServiceMatches =
        service.subServiceUniqueName === subServiceUniqueName;

      if (!serviceMatches || !subServiceMatches) return false;

      return service.subscriptionPeriods.some((period) => {
        try {
          const fromDate = this.parseDate(
            period.activeFromLocalTime,
            period.format,
          );
          const toDate = period.activeToLocalTime
            ? this.parseDate(period.activeToLocalTime, period.format)
            : new Date(9999, 11, 31);

          return now >= fromDate && now <= toDate;
        } catch (error) {
          console.error('Error parsing subscription period dates', error);
          return false;
        }
      });
    });
  }

  private parseDate(dateString: string, format: string): Date {
    if (format === 'd/MM/yyyy') {
      const parts = dateString.split('/');
      if (parts.length !== 3) {
        throw new Error(`Invalid date format: ${dateString}`);
      }
      const dayStr = parts[0] || '';
      const monthStr = parts[1] || '';
      const yearStr = parts[2] || '';

      const day = parseInt(dayStr, 10);
      const month = parseInt(monthStr, 10);
      const year = parseInt(yearStr, 10);

      if (isNaN(day) || isNaN(month) || isNaN(year)) {
        throw new Error(`Invalid date components: ${dateString}`);
      }

      return new Date(year, month - 1, day);
    }

    return new Date(dateString);
  }

  getServiceNameKey(
    serviceUniqueName: string,
    subServiceUniqueName: string,
  ): ServiceName | undefined {
    const serviceString = `${serviceUniqueName}.${subServiceUniqueName}`;

    const entry = Object.entries(SERVICE_NAMES).find(
      ([, value]) => value === serviceString,
    );

    if (entry) {
      return SERVICE_NAMES[entry[0] as keyof typeof SERVICE_NAMES];
    }

    return undefined;
  }

  setPreferredLanguage(language: string) {
    this.user = {
      ...(this.user as User),
      preferredLanguage: language,
    };
  }

  toggleService(serviceName: ServiceName, enabled: boolean) {
    if (!this.user) return;

    if (this.isDefaultService(serviceName) && !enabled) {
      console.warn('Default services cannot be disabled');
      return;
    }

    const [serviceUniqueName, subServiceUniqueName] =
      this.parseServiceName(serviceName);

    const updatedServices = [...this.user.availableServices];
    const serviceIndex = updatedServices.findIndex(
      (service) =>
        service.serviceUniqueName === serviceUniqueName &&
        service.subServiceUniqueName === subServiceUniqueName,
    );

    if (serviceIndex >= 0) {
      const existingService = updatedServices[serviceIndex];
      if (existingService) {
        //add new valid subscriptionPeriod
        existingService.subscriptionPeriods.push({
          activeFromLocalTime: '1/05/2023',
          activeToLocalTime: '1/05/2099',
          name: 'nl-BE',
          format: 'd/MM/yyyy',
        });

        updatedServices[serviceIndex] = {
          active: enabled,
          serviceUniqueName: existingService.serviceUniqueName,
          subServiceUniqueName: existingService.subServiceUniqueName,
          subscriptionPeriods: existingService.subscriptionPeriods,
        };
      }
    } else if (enabled) {
      const newService: UserAccountService = {
        active: true,
        serviceUniqueName: serviceUniqueName,
        subServiceUniqueName: subServiceUniqueName,
        subscriptionPeriods: [
          {
            activeFromLocalTime: new Date().toLocaleDateString('nl-BE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }),
            name: 'nl-BE',
            format: 'd/MM/yyyy',
          },
        ],
      };
      updatedServices.push(newService);
    }

    this.user = {
      ...this.user,
      availableServices: updatedServices,
    };

    this.routerService.refresh('app');
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:user')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('user') declare altName: UserService;`.
declare module '@ember/service' {
  interface Registry {
    user: UserService;
  }
}
